import './AssetItem.css';

import React, { memo } from 'react'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Col, Row } from 'react-bootstrap';
import UserItem from '../common/UserItem';
import { formatDate, setObjectType } from '../../utils';
import AssetRibbon from '../common/AssetRibbon';
import { BdaDataset } from '../../models/BdaDataset';
import { BdaModel } from '../../models/BdaModel';
import { Flex, Tag, Tooltip } from 'antd';
import AppTag from '../common/AppTag';
import AssetCategory from '../common/AssetCategory';

type Props = {
  asset: BdaDataset | BdaModel;
  type: 'dataset' | 'model';
  actions?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onDownload?: React.MouseEventHandler<HTMLButtonElement>;
  onPreview?: React.MouseEventHandler<HTMLButtonElement>;
  onEdit?: React.MouseEventHandler<HTMLButtonElement>;
  onDelete?: React.MouseEventHandler<HTMLButtonElement>;
}

function AssetItem({ asset, type, actions, onClick, onDownload, onPreview, onEdit, onDelete }: Props) {
  const date = formatDate(asset.createDateTime!);
  const icon = type === 'dataset' ? solid('database') : solid('graduation-cap');

  const renderPills = () => {
    if (type === 'dataset') return (
      <Tooltip title="Data source">
        <div className="list-item-pill d-inline-block text-truncate">
          <FontAwesomeIcon icon={solid('database')} />
          <span className="ms-2">{asset.datasource?.name}</span>
        </div>
      </Tooltip>
    );

    return (asset as BdaModel).trainingDataset && (
      <Tooltip title="Training Dataset">
        <div className="list-item-pill d-inline-block text-truncate">
          <FontAwesomeIcon icon={solid('table')} />
          <span className="ms-2">{(asset as BdaModel).trainingDataset?.name}</span>
        </div>
      </Tooltip>
    );
  }

  return (
    <Card className="app-list-item" onClick={onClick}>
      <Card.Header className={"list-item-header bg-" + type}>
        <div className="list-item-icon">
          <FontAwesomeIcon icon={icon} />
        </div>

        <Flex align="start">
          <Col className="list-item-date">
            <b>Created</b>&nbsp;
            <span>{date}</span>
          </Col>

          <Tag color="blue" className="ms-auto shadow text-uppercase font-xs fw-bold m-0">{asset.accessLevel}</Tag>
          {asset.deleteDateTime && (<Tag color="red" className="shadow text-uppercase font-xs fw-bold m-0 ms-2">Deprecated</Tag>)}
        </Flex>

        <div className="header-content">
          <Tooltip title={asset.name} mouseEnterDelay={0.3}>
            <h5 className="list-item-title">{asset.name}</h5>
          </Tooltip>
          {/* <Badge bg="none" className={classNames('asset-mode', 'shadow', modeClass)} pill={true}>
            <FontAwesomeIcon icon={modeIcon} />
            <span className="ms-2">{asset.workflowMode}</span>
          </Badge> */}
          <p className="list-item-description line-clamp-2 mt-2">{asset.description}</p>
        </div>

        <Row className="gx-0 justify-content-center">
          {actions || (
            <Col className="text-center" xs={8}>
              {(asset.created && !asset.deleteDateTime) && (
                <>
                  {(type === "dataset" && (asset as BdaDataset).datasource?.type === "objectStorage" && onDownload) && (
                    <Tooltip title="Download" mouseEnterDelay={0.3}>
                      <button className="circle-button" onClick={onDownload}>
                        <FontAwesomeIcon size="sm" icon={solid('download')} />
                      </button>
                    </Tooltip>
                  )}
                  {type === "dataset" && setObjectType<BdaDataset>(asset) && (
                    <>
                      {asset.datasetFileType!.type === "table" && (
                        <Tooltip title="Preview" mouseEnterDelay={0.3}>
                          <button className="circle-button ms-2" onClick={onPreview}>
                            <FontAwesomeIcon size="sm" icon={solid('eye')} />
                          </button>
                        </Tooltip>
                      )}
                    </>
                  )}
                  <Tooltip title="Edit" mouseEnterDelay={0.3}>
                    <button className="circle-button ms-2" onClick={onEdit}>
                      <FontAwesomeIcon size="sm" icon={solid('pen')} />
                    </button>
                  </Tooltip>
                  <Tooltip title="Delete" mouseEnterDelay={0.3}>
                    <button className="circle-button ms-2" onClick={onDelete}>
                      <FontAwesomeIcon size="sm" icon={solid('trash')} />
                    </button>
                  </Tooltip>
                </>
              )}
              {!asset.created && (
                <button className="circle-button opacity-0">
                  <FontAwesomeIcon size="sm" icon={solid('times')} />
                </button>
              )}
            </Col>
          )}
        </Row>
      </Card.Header>
      <Card.Body className="p-2 position-relative">
        <div className="asset-ribbons">
          {(asset.createdByServiceId === null) && (
            <AssetRibbon
              icon={solid('user')}
              color="info"
              tooltip="This has been created by a user."
            />
          )}
          {(asset.createdByServiceId !== null) && (
            <AssetRibbon
              icon={solid('cogs')}
              color="dark"
              tooltip="This is an output from an execution."
            />
          )}
          {!asset.created && (
            <AssetRibbon
              icon={solid('lock')}
              color="warning"
              tooltip="This has not been created yet."
            />
          )}
        </div>

        <UserItem user={asset.owner!} organization={asset.organization!} className="mb-3" />

        <Row>
          <Col>
            <Flex className="tags-wrapper mb-1">
              {asset.tags.length === 0 && <AppTag noTags />}
              {asset.tags.map(tag => <AppTag className="me-1" tag={tag} key={tag} />)}
            </Flex>
          </Col>
          {asset.category && (
            <Col md="auto">
              <AssetCategory category={asset.category} />
            </Col>
          )}
        </Row>

        <div className="text-center">{renderPills()}</div>
      </Card.Body>
    </Card>
  )
}

export default memo(AssetItem);
